import { Component, Suspense } from 'react';

import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import BMXCameraContainer from '../BMX/containers/BMXCameraContainer';

import { getSelectValue } from '../../utils';
import { formatSelect } from '../Forms/utils';
import { lazyImport } from '../Main/utils';
import { getIsSixteenByNineAspectRatio } from './utils';

import StyledSelect from '../Select';
import { CameraSkeleton } from '../Shared/skeletons';
import {
  StyledDefaultCameraSize,
  StyledNoActiveCameraMessage,
} from './styledComponents';

const EagleEyeVideoEmbedComponent = lazyImport(
  () => import('../EagleEye/EagleEyeVideoEmbedComponent'),
  'EagleEyeVideoEmbedComponent',
);

const NoCamerasComponent = () => (
  <StyledNoActiveCameraMessage>
    This building has no active cameras
  </StyledNoActiveCameraMessage>
);

const CameraContainer = ({ children }) => (
  <Box overflow="hidden">
    <StyledDefaultCameraSize>{children}</StyledDefaultCameraSize>
  </Box>
);

CameraContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

class CallCenterVideoComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedCameraId:
        get(props, `cameras[${props.defaultCameraIndex}].liveViewId`) ||
        get(props, `cameras[0].liveViewId`),
    };
  }

  componentDidMount() {
    const { selectedCameraId } = this.state;
    const { setCurrentCameraId } = this.props;
    setCurrentCameraId(this.getCameraId(selectedCameraId)?.id);
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (!isEqual(this.state, nextState) || !isEqual(this.props, nextProps)) {
      return true;
    }
    return false;
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectedCameraId } = this.state;
    const { selectedCameraId: prevSelectedCameraId } = prevState;
    const { setCurrentCameraId } = this.props;
    if (selectedCameraId !== prevSelectedCameraId) {
      setCurrentCameraId(this.getCameraId(selectedCameraId)?.id);
    }
  }

  getCameraId = (currentLiveViewId) => {
    const { cameras } = this.props;
    return cameras?.find(({ liveViewId }) => liveViewId === currentLiveViewId);
  };

  render() {
    const { selectedCameraId } = this.state;
    const { cameras, buildingId, buildingName } = this.props;
    let video;
    const selectedCamera =
      selectedCameraId &&
      cameras &&
      cameras.find((camera) => camera.liveViewId === selectedCameraId);

    if (!selectedCamera?.id) return <NoCamerasComponent />;

    const provider = get(selectedCamera, 'provider', null);
    const eagleEyeCamera = (
      <CameraContainer>
        <EagleEyeVideoEmbedComponent
          cameraId={selectedCameraId}
          cameraName={selectedCamera.name}
          buildingId={buildingId}
          buildingName={buildingName}
          isSixteenByNineAspectRatio={getIsSixteenByNineAspectRatio(
            selectedCamera,
          )}
        />
      </CameraContainer>
    );
    const bmxCamera = (
      <CameraContainer>
        <BMXCameraContainer
          camera={selectedCamera}
          cameraName={selectedCamera.name}
          buildingId={buildingId}
          buildingName={buildingName}
        />
      </CameraContainer>
    );
    const formattedCameras = formatSelect(cameras, 'name', 'liveViewId');
    if (provider === 'bmx') {
      video = bmxCamera;
    } else {
      video = eagleEyeCamera;
    }
    return (
      <Box position="relative">
        <Suspense fallback={<CameraSkeleton />}>{video || null}</Suspense>
        <Box height="1rem" />
        {formattedCameras?.length > 1 ? (
          <StyledSelect
            value={getSelectValue(selectedCameraId, formattedCameras)}
            onChange={(event, option) => {
              this.setState({ selectedCameraId: option.value });
            }}
            options={formattedCameras}
            disableClearable
          />
        ) : (
          <p>{selectedCamera?.name}</p>
        )}
      </Box>
    );
  }
}

CallCenterVideoComponent.defaultProps = {
  defaultCameraIndex: null,
  buildingName: '',
};

CallCenterVideoComponent.propTypes = {
  buildingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  cameras: PropTypes.instanceOf(Array).isRequired,
  defaultCameraIndex: PropTypes.number,
  setCurrentCameraId: PropTypes.func.isRequired,
  buildingName: PropTypes.string,
};

export default CallCenterVideoComponent;
